export const ORDER_CROSS_TAB_EVENT_TYPE = "ORDER_CHANGED" as const;
export const ORDER_CROSS_TAB_CHANNEL = "alotruck:orders";
export const ORDER_CROSS_TAB_STORAGE_KEY = "alotruck:orders:changed";

export type OrderCrossTabAction =
  | "create"
  | "update"
  | "requestEdit"
  | "finish"
  | "delete";

export type OrderCrossTabEvent = {
  type: typeof ORDER_CROSS_TAB_EVENT_TYPE;
  action: OrderCrossTabAction;
  orderId?: string | null;
  nextStatus?: string | null;
  sourceId: string;
  timestamp: number;
};

export type OrderCrossTabEventInput = Omit<
  OrderCrossTabEvent,
  "type" | "sourceId" | "timestamp"
>;

const orderStatusToTab: Record<string, string> = {
  PENDING_APPROVAL: "1",
  SHIPPING: "2",
  FINISHED: "3",
};

const createOrderTabSourceId = () =>
  `orders-${Date.now()}-${Math.random().toString(36).slice(2)}`;

export const ORDER_CROSS_TAB_SOURCE_ID = createOrderTabSourceId();

export const getOrderTabByStatus = (status?: string | null) =>
  status ? orderStatusToTab[status] ?? null : null;

export const createOrderCrossTabEvent = (
  input: OrderCrossTabEventInput,
  sourceId = ORDER_CROSS_TAB_SOURCE_ID,
  getTimestamp = Date.now,
): OrderCrossTabEvent => ({
  type: ORDER_CROSS_TAB_EVENT_TYPE,
  ...input,
  sourceId,
  timestamp: getTimestamp(),
});

const isOrderCrossTabEvent = (event: unknown): event is OrderCrossTabEvent =>
  !!event &&
  typeof event === "object" &&
  (event as OrderCrossTabEvent).type === ORDER_CROSS_TAB_EVENT_TYPE;

const getBrowserWindow = () =>
  typeof window === "undefined" ? null : window;

export const shouldReloadOrderTabFromEvent = (
  activeTab: string,
  event: OrderCrossTabEvent,
  sourceId = ORDER_CROSS_TAB_SOURCE_ID,
) =>
  isOrderCrossTabEvent(event) &&
  event.sourceId !== sourceId &&
  getOrderTabByStatus(event.nextStatus) === activeTab;

export const emitOrderCrossTabEvent = (
  input: OrderCrossTabEventInput,
  targetWindow = getBrowserWindow(),
) => {
  const event = createOrderCrossTabEvent(input);

  if (!targetWindow) return event;

  if (typeof targetWindow.BroadcastChannel === "function") {
    const channel = new targetWindow.BroadcastChannel(
      ORDER_CROSS_TAB_CHANNEL,
    );
    channel.postMessage(event);
    channel.close();
    return event;
  }

  try {
    targetWindow.localStorage.setItem(
      ORDER_CROSS_TAB_STORAGE_KEY,
      JSON.stringify(event),
    );
  } catch {
    // Ignore storage errors; the current tab already updated its local state.
  }

  return event;
};

export const subscribeOrderCrossTabEvents = (
  onEvent: (event: OrderCrossTabEvent) => void,
  targetWindow = getBrowserWindow(),
) => {
  if (!targetWindow) return () => {};

  if (typeof targetWindow.BroadcastChannel === "function") {
    const channel = new targetWindow.BroadcastChannel(
      ORDER_CROSS_TAB_CHANNEL,
    );

    channel.onmessage = (message) => {
      if (isOrderCrossTabEvent(message.data)) {
        onEvent(message.data);
      }
    };

    return () => channel.close();
  }

  const handleStorage = (event: StorageEvent) => {
    if (
      event.key !== ORDER_CROSS_TAB_STORAGE_KEY ||
      typeof event.newValue !== "string"
    ) {
      return;
    }

    try {
      const payload = JSON.parse(event.newValue);

      if (isOrderCrossTabEvent(payload)) {
        onEvent(payload);
      }
    } catch {
      // Ignore malformed values written by another tab or browser extension.
    }
  };

  targetWindow.addEventListener("storage", handleStorage);
  return () => targetWindow.removeEventListener("storage", handleStorage);
};
